import React, { useState, useContext, useEffect, Fragment, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { db } from "../firebase";
import UserContext from "../context/UserContext";
import { LEARNING_CATEGORIES } from "../utils/categories";

const Admin = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [content, setContent] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [imageUrls, setImageUrls] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [furtherReading, setFurtherReading] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editContent, setEditContent] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [editImageUrls, setEditImageUrls] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [editFurtherReading, setEditFurtherReading] = useState({
    kids: "",
    teens: "",
    adults: ""
  });
  const [launchImages, setLaunchImages] = useState([]);
  const [newImageType, setNewImageType] = useState("provider");
  const [newImageName, setNewImageName] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [editingImage, setEditingImage] = useState(null);
  const [editImageType, setEditImageType] = useState("");
  const [editImageName, setEditImageName] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [isArticlesExpanded, setIsArticlesExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isImagesExpanded, setIsImagesExpanded] = useState(false);
  const [isCreateExpanded, setIsCreateExpanded] = useState(false);
  const [createMaxHeight, setCreateMaxHeight] = useState(0);
  const [editMaxHeight, setEditMaxHeight] = useState(0);
  const [youtubeChannels, setYoutubeChannels] = useState([]);
  const [isChannelsExpanded, setIsChannelsExpanded] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelId, setNewChannelId] = useState("");
  const [newChannelHandle, setNewChannelHandle] = useState("");
  const [editingChannel, setEditingChannel] = useState(null);
  const [editChannelName, setEditChannelName] = useState("");
  const [editChannelId, setEditChannelId] = useState("");
  const [editChannelHandle, setEditChannelHandle] = useState("");
  const [userCount, setUserCount] = useState(0);

  // Refs for auto-sizing textareas
  const kidsTextareaRef = useRef(null);
  const teensTextareaRef = useRef(null);
  const adultsTextareaRef = useRef(null);
  const createKidsTextareaRef = useRef(null);
  const createTeensTextareaRef = useRef(null);
  const createAdultsTextareaRef = useRef(null);

  // Auto-size textareas when content changes
  useEffect(() => {
    const autoSizeTextarea = (textarea) => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    };

    autoSizeTextarea(kidsTextareaRef.current);
    autoSizeTextarea(teensTextareaRef.current);
    autoSizeTextarea(adultsTextareaRef.current);
  }, [editContent.kids, editContent.teens, editContent.adults]);

  // Auto-size create form textareas when content changes
  useEffect(() => {
    const autoSizeTextarea = (textarea) => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    };

    autoSizeTextarea(createKidsTextareaRef.current);
    autoSizeTextarea(createTeensTextareaRef.current);
    autoSizeTextarea(createAdultsTextareaRef.current);
  }, [content.kids, content.teens, content.adults]);

  // Calculate max height for create section textareas
  useEffect(() => {
    const heights = [
      createKidsTextareaRef.current?.scrollHeight || 0,
      createTeensTextareaRef.current?.scrollHeight || 0,
      createAdultsTextareaRef.current?.scrollHeight || 0
    ];
    const maxHeight = Math.max(...heights);
    setCreateMaxHeight(maxHeight);
  }, [content.kids, content.teens, content.adults]);

  // Calculate max height for edit section textareas
  useEffect(() => {
    const heights = [
      kidsTextareaRef.current?.scrollHeight || 0,
      teensTextareaRef.current?.scrollHeight || 0,
      adultsTextareaRef.current?.scrollHeight || 0
    ];
    const maxHeight = Math.max(...heights);
    setEditMaxHeight(maxHeight);
  }, [editContent.kids, editContent.teens, editContent.adults]);

  // Check if user is admin
  const isAdmin = user && user.email === "adamkfrancis1989@gmail.com";

  // Fetch user count on component mount
  useEffect(() => {
    if (isAdmin) {
      const fetchUserCount = async () => {
        try {
          const getUserCountFunction = httpsCallable(functions, 'getUserCount');
          const result = await getUserCountFunction();
          setUserCount(result.data.userCount);
        } catch (error) {
          console.error("Error fetching user count:", error);
        }
      };

      fetchUserCount();
    }
  }, [isAdmin]);

  // Fetch articles when expanded
  useEffect(() => {
    if (isAdmin && isArticlesExpanded) {
      fetchArticles();
    }
  }, [isAdmin, isArticlesExpanded]);

  // Fetch launch images when expanded
  useEffect(() => {
    if (isAdmin && isImagesExpanded) {
      fetchLaunchImages();
    }
  }, [isAdmin, isImagesExpanded]);

  // Fetch YouTube channels when expanded
  useEffect(() => {
    if (isAdmin && isChannelsExpanded) {
      fetchYoutubeChannels();
    }
  }, [isAdmin, isChannelsExpanded]);

  const fetchArticles = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "learn"));
      const articlesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArticles(articlesData);
    } catch (error) {
      console.error("Error fetching articles:", error);
    }
  };

  const fetchLaunchImages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "launchImages"));
      const imagesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLaunchImages(imagesData);
    } catch (error) {
      console.error("Error fetching launch images:", error);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setEditTitle(article.title);
    setEditCategory(article.category || "");
    setEditContent({
      kids: article.content?.kids || "",
      teens: article.content?.teens || "",
      adults: article.content?.adults || ""
    });
    setEditImageUrls({
      kids: article.imageUrls?.kids || "",
      teens: article.imageUrls?.teens || "",
      adults: article.imageUrls?.adults || ""
    });
    setEditFurtherReading({
      kids: article.furtherReading?.kids || "",
      teens: article.furtherReading?.teens || "",
      adults: article.furtherReading?.adults || ""
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!editTitle.trim()) {
        throw new Error("Please provide a title.");
      }

      const updatedData = {
        title: editTitle.trim(),
        category: editCategory,
        content: {
          kids: editContent.kids.trim() || editContent.adults.trim() || editContent.teens.trim(),
          teens: editContent.teens.trim() || editContent.adults.trim() || editContent.kids.trim(),
          adults: editContent.adults.trim() || editContent.teens.trim() || editContent.kids.trim()
        },
        imageUrls: {
          kids: editImageUrls.kids.trim(),
          teens: editImageUrls.teens.trim(),
          adults: editImageUrls.adults.trim()
        },
        furtherReading: {
          kids: editFurtherReading.kids.trim(),
          teens: editFurtherReading.teens.trim(),
          adults: editFurtherReading.adults.trim()
        }
      };

      await updateDoc(doc(db, "learn", editingArticle.id), updatedData);

      setMessage("Article updated successfully!");
      setEditingArticle(null);
      setEditTitle("");
      setEditContent({ kids: "", teens: "", adults: "" });
      setEditImageUrls({ kids: "", teens: "", adults: "" });
      setEditFurtherReading({ kids: "", teens: "", adults: "" });
      fetchArticles(); // Refresh the list

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error updating article:", error);
      setMessage("Error updating article: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "learn", articleId));
      setMessage("Article deleted successfully!");
      fetchArticles(); // Refresh the list
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting article:", error);
      setMessage("Error deleting article: " + error.message);
    }
  };

  const cancelEdit = () => {
    setEditingArticle(null);
    setEditTitle("");
    setEditCategory("");
    setEditContent({ kids: "", teens: "", adults: "" });
    setEditImageUrls({ kids: "", teens: "", adults: "" });
    setEditFurtherReading({ kids: "", teens: "", adults: "" });
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Group articles by category
  const groupedArticles = articles.reduce((acc, article) => {
    const category = article.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(article);
    return acc;
  }, {});

  // Get category info for display
  const getCategoryInfo = (categoryId) => {
    if (categoryId === 'uncategorized') {
      return { title: 'Uncategorized', icon: '📝' };
    }
    return LEARNING_CATEGORIES.find(cat => cat.id === categoryId) || { title: categoryId, icon: '📝' };
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!newImageName.trim() || !newImageUrl.trim()) {
        throw new Error("Please provide name and URL.");
      }

      await addDoc(collection(db, "launchImages"), {
        type: newImageType,
        name: newImageName.trim(),
        url: newImageUrl.trim()
      });

      setMessage("Image added successfully!");
      setNewImageName("");
      setNewImageUrl("");
      fetchLaunchImages();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error adding image:", error);
      setMessage("Error adding image: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
    setEditImageType(image.type);
    setEditImageName(image.name);
    setEditImageUrl(image.url);
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!editImageName.trim() || !editImageUrl.trim()) {
        throw new Error("Please provide name and URL.");
      }

      await updateDoc(doc(db, "launchImages", editingImage.id), {
        type: editImageType,
        name: editImageName.trim(),
        url: editImageUrl.trim()
      });

      setMessage("Image updated successfully!");
      setEditingImage(null);
      setEditImageType("");
      setEditImageName("");
      setEditImageUrl("");
      fetchLaunchImages();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error updating image:", error);
      setMessage("Error updating image: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "launchImages", imageId));
      setMessage("Image deleted successfully!");
      fetchLaunchImages();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting image:", error);
      setMessage("Error deleting image: " + error.message);
    }
  };

  const cancelEditImage = () => {
    setEditingImage(null);
    setEditImageType("");
    setEditImageName("");
    setEditImageUrl("");
  };

  const fetchYoutubeChannels = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "youtubeChannels"));
      const channelsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setYoutubeChannels(channelsData);
    } catch (error) {
      console.error("Error fetching YouTube channels:", error);
    }
  };

  const handleAddChannel = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!newChannelName.trim() || !newChannelId.trim()) {
        throw new Error("Please provide channel name and ID.");
      }

      await addDoc(collection(db, "youtubeChannels"), {
        name: newChannelName.trim(),
        channelId: newChannelId.trim(),
        handle: newChannelHandle.trim() || "",
        active: true
      });

      setMessage("Channel added successfully!");
      setNewChannelName("");
      setNewChannelId("");
      setNewChannelHandle("");
      fetchYoutubeChannels();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error adding channel:", error);
      setMessage("Error adding channel: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChannel = (channel) => {
    setEditingChannel(channel);
    setEditChannelName(channel.name);
    setEditChannelId(channel.channelId);
    setEditChannelHandle(channel.handle || "");
  };

  const handleUpdateChannel = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!editChannelName.trim() || !editChannelId.trim()) {
        throw new Error("Please provide channel name and ID.");
      }

      await updateDoc(doc(db, "youtubeChannels", editingChannel.id), {
        name: editChannelName.trim(),
        channelId: editChannelId.trim(),
        handle: editChannelHandle.trim() || ""
      });

      setMessage("Channel updated successfully!");
      setEditingChannel(null);
      setEditChannelName("");
      setEditChannelId("");
      setEditChannelHandle("");
      fetchYoutubeChannels();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error updating channel:", error);
      setMessage("Error updating channel: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChannel = async (channelId, currentActive) => {
    try {
      await updateDoc(doc(db, "youtubeChannels", channelId), {
        active: !currentActive
      });
      fetchYoutubeChannels();
      setMessage(`Channel ${!currentActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling channel:", error);
      setMessage("Error toggling channel: " + error.message);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm("Are you sure you want to delete this channel?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "youtubeChannels", channelId));
      setMessage("Channel deleted successfully!");
      fetchYoutubeChannels();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting channel:", error);
      setMessage("Error deleting channel: " + error.message);
    }
  };

  const initializeChannels = async () => {
    const defaultChannels = [
      { name: "SpaceX", channelId: "UCtI0Hodo5o5dUb67FeUjDeA" },
      { name: "Everyday Astronaut", channelId: "UC6uKrU_WqJRa6Cdn4i8IArOA" },
      { name: "Scott Manley", channelId: "UCoO-1Jo4dXtxW8DKOxsk1uQ" },
      { name: "Rocket Lab", channelId: "UCNB3o-WMIIq7Q8T4oQ7CeAA" }
    ];

    try {
      for (const channel of defaultChannels) {
        await addDoc(collection(db, "youtubeChannels"), {
          ...channel,
          active: true
        });
      }
      setMessage("Default channels initialized successfully!");
      fetchYoutubeChannels();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error initializing channels:", error);
      setMessage("Error initializing channels: " + error.message);
    }
  };

  const cancelEditChannel = () => {
    setEditingChannel(null);
    setEditChannelName("");
    setEditChannelId("");
    setEditChannelHandle("");
  };

  if (!isAdmin) {
    return (
      <div className="p-4 max-w-2xl mx-auto min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      // Validate that at least one content field is filled
      if (!content.kids.trim() && !content.teens.trim() && !content.adults.trim()) {
        throw new Error("Please provide content for at least one age group.");
      }

      const articleData = {
        title: title.trim(),
        category: category,
        content: {
          kids: content.kids.trim() || content.adults.trim() || content.teens.trim(), // Fallback to other content if empty
          teens: content.teens.trim() || content.adults.trim() || content.kids.trim(),
          adults: content.adults.trim() || content.teens.trim() || content.kids.trim()
        },
        imageUrls: {
          kids: imageUrls.kids.trim(),
          teens: imageUrls.teens.trim(),
          adults: imageUrls.adults.trim()
        },
        furtherReading: {
          kids: furtherReading.kids.trim(),
          teens: furtherReading.teens.trim(),
          adults: furtherReading.adults.trim()
        },
        created_date: serverTimestamp(),
        author: user.email
      };

      await addDoc(collection(db, "learn"), articleData);

      setMessage("Article created successfully!");
      setTitle("");
      setCategory("");
      setContent({ kids: "", teens: "", adults: "" });
      setImageUrls({ kids: "", teens: "", adults: "" });
      setFurtherReading({ kids: "", teens: "", adults: "" });

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error creating article:", error);
      setMessage("Error creating article: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <div className="text-right">
          <div className="text-sm text-white">Registered Users</div>
          <div className="text-2xl font-bold text-blue-600">{userCount}</div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <button
          onClick={() => navigate('/admin/f1-dashboard')}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 font-semibold"
        >
          F1 Dashboard
        </button>
        <button
          onClick={() => navigate('/admin/sales-prospecting')}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 font-semibold"
        >
          Sales Prospecting Dashboard
        </button>
      </div>



      <div className="bg-silver p-6 rounded-custom border border-gray-300">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center justify-between">
          Create Learning Article
          <button
            onClick={() => setIsCreateExpanded(!isCreateExpanded)}
            className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
          >
            {isCreateExpanded ? 'Collapse' : 'Expand'}
          </button>
        </h2>

        {isCreateExpanded && (
          <Fragment>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-800 font-semibold mb-2">Article Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter article title..."
                  required
                />
              </div>

              <div>
                <label className="block text-gray-800 font-semibold mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a category...</option>
                  {LEARNING_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Kids Content (Ages 1-12)</label>
                    <textarea
                      ref={createKidsTextareaRef}
                      value={content.kids}
                      onChange={(e) => setContent(prev => ({ ...prev, kids: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32 resize-vertical"
                      placeholder="Enter content for kids..."
                      style={{ height: createMaxHeight ? `${createMaxHeight}px` : 'auto' }}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Kids Image URL</label>
                    <input
                      type="url"
                      value={imageUrls.kids}
                      onChange={(e) => setImageUrls(prev => ({ ...prev, kids: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Kids Further Reading</label>
                    <input
                      type="url"
                      value={furtherReading.kids}
                      onChange={(e) => setFurtherReading(prev => ({ ...prev, kids: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/learn-more"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Teens Content (Ages 13-17)</label>
                    <textarea
                      ref={createTeensTextareaRef}
                      value={content.teens}
                      onChange={(e) => setContent(prev => ({ ...prev, teens: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32 resize-vertical"
                      placeholder="Enter content for teens..."
                      style={{ height: createMaxHeight ? `${createMaxHeight}px` : 'auto' }}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Teens Image URL</label>
                    <input
                      type="url"
                      value={imageUrls.teens}
                      onChange={(e) => setImageUrls(prev => ({ ...prev, teens: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Teens Further Reading</label>
                    <input
                      type="url"
                      value={furtherReading.teens}
                      onChange={(e) => setFurtherReading(prev => ({ ...prev, teens: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/learn-more"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Adults Content (Ages 18+)</label>
                    <textarea
                      ref={createAdultsTextareaRef}
                      value={content.adults}
                      onChange={(e) => setContent(prev => ({ ...prev, adults: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32 resize-vertical"
                      placeholder="Enter content for adults..."
                      style={{ height: createMaxHeight ? `${createMaxHeight}px` : 'auto' }}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Adults Image URL</label>
                    <input
                      type="url"
                      value={imageUrls.adults}
                      onChange={(e) => setImageUrls(prev => ({ ...prev, adults: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-800 font-semibold mb-2">Adults Further Reading</label>
                    <input
                      type="url"
                      value={furtherReading.adults}
                      onChange={(e) => setFurtherReading(prev => ({ ...prev, adults: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/learn-more"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-kids-blue text-white px-6 py-3 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? "Creating..." : "Create Article"}
                </button>
              </div>
            </form>

            {message && (
              <div className={`mt-4 p-3 rounded-custom ${
                message.includes("Error")
                  ? "bg-red-100 border border-red-400 text-red-700"
                  : "bg-green-100 border border-green-400 text-green-700"
              }`}>
                {message}
              </div>
            )}
          </Fragment>
        )}
      </div>

      {/* Edit Article Section */}
      {editingArticle && (
        <div className="bg-silver p-6 rounded-custom border border-gray-300 mt-6">
          <h2 className="text-2xl font-bold mb-4">Edit Article: {editingArticle.title}</h2>

          <form onSubmit={handleUpdate} className="space-y-6">
            <div>
              <label className="block text-gray-800 font-semibold mb-2">Article Title</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter article title..."
                required
              />
            </div>

            <div>
              <label className="block text-gray-800 font-semibold mb-2">Category</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a category...</option>
                {LEARNING_CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Kids Content (Ages 1-12)</label>
                  <textarea
                    ref={kidsTextareaRef}
                    value={editContent.kids}
                    onChange={(e) => setEditContent(prev => ({ ...prev, kids: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-48 resize-vertical"
                    placeholder="Enter content for kids..."
                    style={{ height: editMaxHeight ? `${editMaxHeight}px` : 'auto' }}
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Kids Image URL</label>
                  <input
                    type="url"
                    value={editImageUrls.kids}
                    onChange={(e) => setEditImageUrls(prev => ({ ...prev, kids: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Kids Further Reading</label>
                  <input
                    type="url"
                    value={editFurtherReading.kids}
                    onChange={(e) => setEditFurtherReading(prev => ({ ...prev, kids: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/learn-more"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Teens Content (Ages 13-17)</label>
                  <textarea
                    ref={teensTextareaRef}
                    value={editContent.teens}
                    onChange={(e) => setEditContent(prev => ({ ...prev, teens: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-48 resize-vertical"
                    placeholder="Enter content for teens..."
                    style={{ height: editMaxHeight ? `${editMaxHeight}px` : 'auto' }}
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Teens Image URL</label>
                  <input
                    type="url"
                    value={editImageUrls.teens}
                    onChange={(e) => setEditImageUrls(prev => ({ ...prev, teens: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Teens Further Reading</label>
                  <input
                    type="url"
                    value={editFurtherReading.teens}
                    onChange={(e) => setEditFurtherReading(prev => ({ ...prev, teens: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/learn-more"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Adults Content (Ages 18+)</label>
                  <textarea
                    ref={adultsTextareaRef}
                    value={editContent.adults}
                    onChange={(e) => setEditContent(prev => ({ ...prev, adults: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-48 resize-vertical"
                    placeholder="Enter content for adults..."
                    style={{ height: editMaxHeight ? `${editMaxHeight}px` : 'auto' }}
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Adults Image URL</label>
                  <input
                    type="url"
                    value={editImageUrls.adults}
                    onChange={(e) => setEditImageUrls(prev => ({ ...prev, adults: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Adults Further Reading</label>
                  <input
                    type="url"
                    value={editFurtherReading.adults}
                    onChange={(e) => setEditFurtherReading(prev => ({ ...prev, adults: e.target.value }))}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/learn-more"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={cancelEdit}
                className="bg-gray-500 text-white px-6 py-3 rounded-custom hover:bg-gray-600 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-kids-blue text-white px-6 py-3 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? "Updating..." : "Update Article"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Articles Section */}
      <div className="bg-silver p-6 rounded-custom border border-gray-300 mt-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center justify-between">
          Existing Articles {isArticlesExpanded ? `(${articles.length})` : ''}
          <button
            onClick={() => setIsArticlesExpanded(!isArticlesExpanded)}
            className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
          >
            {isArticlesExpanded ? 'Collapse' : 'Expand'}
          </button>
        </h2>

        {isArticlesExpanded && (
          <Fragment>
            {articles.length === 0 ? (
              <p className="text-gray-600">No articles found.</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedArticles).map(([categoryId, categoryArticles]) => {
                  const categoryInfo = getCategoryInfo(categoryId);
                  const isExpanded = expandedCategories[categoryId];

                  return (
                    <div key={categoryId} className="bg-white rounded-custom border border-gray-200 overflow-hidden">
                      <div
                        className="p-4 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                        onClick={() => toggleCategoryExpansion(categoryId)}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{categoryInfo.icon}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{categoryInfo.title}</h3>
                            <p className="text-sm text-gray-600">{categoryArticles.length} articles</p>
                          </div>
                        </div>
                        <span className={`text-xl transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          ▶
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="p-4 space-y-3">
                          {categoryArticles.map(article => (
                            <div key={article.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded border">
                              <span className="text-gray-800 font-medium flex-1">{article.title}</span>
                              <div className="flex space-x-2 ml-4">
                                <button
                                  onClick={() => handleEdit(article)}
                                  className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm font-semibold"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(article.id)}
                                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm font-semibold"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Fragment>
        )}
      </div>

      {/* Manage Launch Images Section */}
      <div className="bg-silver p-6 rounded-custom border border-gray-300 mt-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center justify-between">
          Manage Launch Images
          <button
            onClick={() => setIsImagesExpanded(!isImagesExpanded)}
            className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
          >
            {isImagesExpanded ? 'Collapse' : 'Expand'}
          </button>
        </h2>

        {isImagesExpanded && (
          <Fragment>
            <form onSubmit={handleAddImage} className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Type</label>
                  <select
                    value={newImageType}
                    onChange={(e) => setNewImageType(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="provider">Provider</option>
                    <option value="rocket">Rocket</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Name</label>
                  <input
                    type="text"
                    value={newImageName}
                    onChange={(e) => setNewImageName(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., SpaceX"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Image URL</label>
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-kids-blue text-white px-6 py-3 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? "Adding..." : "Add Image"}
                </button>
              </div>
            </form>

            {editingImage && (
              <div className="bg-white p-4 rounded-custom border border-gray-200 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Image</h3>
                <form onSubmit={handleUpdateImage} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Type</label>
                      <select
                        value={editImageType}
                        onChange={(e) => setEditImageType(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="provider">Provider</option>
                        <option value="rocket">Rocket</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Name</label>
                      <input
                        type="text"
                        value={editImageName}
                        onChange={(e) => setEditImageName(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Image URL</label>
                      <input
                        type="url"
                        value={editImageUrl}
                        onChange={(e) => setEditImageUrl(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={cancelEditImage}
                      className="bg-gray-500 text-white px-4 py-2 rounded-custom hover:bg-gray-600 font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                    >
                      Update Image
                    </button>
                  </div>
                </form>
              </div>
            )}

            <h3 className="text-xl font-bold mb-4 text-gray-800">Existing Images ({launchImages.length})</h3>
            {launchImages.length === 0 ? (
              <p className="text-gray-600">No images found.</p>
            ) : (
              <div className="space-y-4">
                {launchImages.map(image => (
                  <div key={image.id} className="bg-white p-4 rounded-custom border border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-16 h-16 object-contain"
                          onError={(e) => { e.target.src = "https://placehold.co/64x64?text=Error"; }}
                        />
                        <div>
                          <p className="text-gray-800 font-semibold">{image.name}</p>
                          <p className="text-gray-600 text-sm capitalize">{image.type}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditImage(image)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteImage(image.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-custom hover:bg-red-600 text-sm font-semibold"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Fragment>
        )}
      </div>

      {/* Manage YouTube Channels Section */}
      <div className="bg-silver p-6 rounded-custom border border-gray-300 mt-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800 flex items-center justify-between">
          Manage YouTube Channels
          <button
            onClick={() => setIsChannelsExpanded(!isChannelsExpanded)}
            className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
          >
            {isChannelsExpanded ? 'Collapse' : 'Expand'}
          </button>
        </h2>

        {isChannelsExpanded && (
          <Fragment>
            <div className="mb-6">
              <button
                onClick={initializeChannels}
                className="bg-green-500 text-white px-4 py-2 rounded-custom hover:bg-green-600 text-sm font-semibold mb-4"
              >
                Initialize Default Channels
              </button>
              <p className="text-sm text-gray-600">This will add SpaceX, Everyday Astronaut, Scott Manley, and Rocket Lab channels.</p>
            </div>

            <form onSubmit={handleAddChannel} className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Channel Name</label>
                  <input
                    type="text"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., SpaceX"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Channel ID</label>
                  <input
                    type="text"
                    value={newChannelId}
                    onChange={(e) => setNewChannelId(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., UCuI0Hodo5o5dUb67FeUjDeA"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-2">Handle</label>
                  <input
                    type="text"
                    value={newChannelHandle}
                    onChange={(e) => setNewChannelHandle(e.target.value)}
                    className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., @esaeducation"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-kids-blue text-white px-6 py-3 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? "Adding..." : "Add Channel"}
                </button>
              </div>
            </form>

            {editingChannel && (
              <div className="bg-white p-4 rounded-custom border border-gray-200 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Channel: {editingChannel.name}</h3>
                <form onSubmit={handleUpdateChannel} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Channel Name</label>
                      <input
                        type="text"
                        value={editChannelName}
                        onChange={(e) => setEditChannelName(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Channel ID</label>
                      <input
                        type="text"
                        value={editChannelId}
                        onChange={(e) => setEditChannelId(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">Handle</label>
                      <input
                        type="text"
                        value={editChannelHandle}
                        onChange={(e) => setEditChannelHandle(e.target.value)}
                        className="w-full p-3 bg-white text-gray-800 rounded-custom border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., @esaeducation"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={cancelEditChannel}
                      className="bg-gray-500 text-white px-6 py-3 rounded-custom hover:bg-gray-600 font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-kids-blue text-white px-6 py-3 rounded-custom hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                    >
                      Update Channel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <h3 className="text-xl font-bold mb-4 text-gray-800">Existing Channels ({youtubeChannels.length})</h3>
            {youtubeChannels.length === 0 ? (
              <p className="text-gray-600">No channels found. Click "Initialize Default Channels" to add the default channels.</p>
            ) : (
              <div className="space-y-4">
                {youtubeChannels.map(channel => (
                  <div key={channel.id} className="bg-white p-4 rounded-custom border border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="text-gray-800 font-semibold">{channel.name}</p>
                          <p className="text-gray-600 text-sm">{channel.channelId}</p>
                          {channel.handle && <p className="text-gray-600 text-sm">{channel.handle}</p>}
                          <p className={`text-sm ${channel.active ? 'text-green-600' : 'text-red-600'}`}>
                            {channel.active ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleChannel(channel.id, channel.active)}
                          className={`px-3 py-1 rounded text-sm font-semibold ${
                            channel.active
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {channel.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditChannel(channel)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-custom hover:bg-blue-600 text-sm font-semibold"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteChannel(channel.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-custom hover:bg-red-600 text-sm font-semibold"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default Admin;
